import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, MagnifyingGlass, ArrowsDownUp } from '@phosphor-icons/react';
import { api } from '../../lib/api-client';
import SceneCard from './SceneCard';
import type { Scene } from '@spine-workspace/shared';
import { CANVAS_PRESETS } from '@spine-workspace/shared';

const inputCls = `w-full bg-bg-input border border-border text-text-primary rounded-md
  px-3 py-[7px] text-sm font-sans placeholder:text-text-tertiary
  hover:bg-bg-hover focus:outline-none focus:border-accent focus:shadow-focus
  transition-all duration-normal`;

type SortKey = 'updated' | 'created' | 'name';

export default function SceneGrid() {
  const navigate = useNavigate();
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [canvasPreset, setCanvasPreset] = useState('1920x1080');
  const [customW, setCustomW] = useState(1920);
  const [customH, setCustomH] = useState(1080);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('updated');

  const loadScenes = useCallback(async () => {
    try {
      const res = await api.get<{ scenes: Scene[] }>('/api/scenes');
      setScenes(res.scenes);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadScenes(); }, [loadScenes]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);

    let w = customW, h = customH;
    if (canvasPreset !== 'custom') {
      const preset = CANVAS_PRESETS[canvasPreset];
      w = preset.width;
      h = preset.height;
    }

    try {
      const res = await api.post<{ scene: Scene }>('/api/scenes', {
        name: newName.trim(),
        canvasWidth: w,
        canvasHeight: h,
      });
      setScenes((prev) => [res.scene, ...prev]);
      setShowCreate(false);
      setNewName('');
      navigate(`/scene/${res.scene.id}/edit`);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create scene');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.delete(`/api/scenes/${id}`);
      setScenes((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete scene');
    }
  }

  async function handleRename(id: string, name: string) {
    try {
      const scene = scenes.find((s) => s.id === id);
      if (!scene) return;
      await api.put(`/api/scenes/${id}`, { sceneData: scene.sceneData, name });
      setScenes((prev) => prev.map((s) => s.id === id ? { ...s, name } : s));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to rename scene');
    }
  }

  async function handleDuplicate(id: string) {
    const src = scenes.find((s) => s.id === id);
    if (!src) return;
    try {
      const res = await api.post<{ scene: Scene }>('/api/scenes', {
        name: `${src.name} (copy)`,
        canvasWidth: src.canvasWidth,
        canvasHeight: src.canvasHeight,
        sceneData: src.sceneData,
      });
      setScenes((prev) => [res.scene, ...prev]);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to duplicate scene');
    }
  }

  // Filter + sort
  const filteredScenes = scenes
    .filter((s) => !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name);
      if (sortKey === 'created') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-text-tertiary font-sans text-sm">
        Loading scenes…
      </div>
    );
  }

  return (
    <div>
      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-modal p-4">
          <div className="bg-bg-elevated border border-border-subtle rounded-xl p-6 w-full max-w-sm shadow-elevated">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-lg font-medium text-text-primary">New Scene</h2>
              <button
                onClick={() => setShowCreate(false)}
                className="w-7 h-7 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast"
              >
                <X size={14} />
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block font-sans text-xs font-medium text-text-secondary mb-1.5">Scene name</label>
                <input
                  className={inputCls}
                  autoFocus
                  placeholder="My Scene"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="block font-sans text-xs font-medium text-text-secondary mb-1.5">Canvas size</label>
                <select
                  className={`${inputCls} appearance-none`}
                  value={canvasPreset}
                  onChange={(e) => setCanvasPreset(e.target.value)}
                >
                  {Object.entries(CANVAS_PRESETS).map(([key, p]) => (
                    <option key={key} value={key}>{p.label}</option>
                  ))}
                  <option value="custom">Custom…</option>
                </select>
              </div>

              {canvasPreset === 'custom' && (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block font-sans text-xs font-medium text-text-secondary mb-1.5">Width</label>
                    <input
                      className={inputCls}
                      type="number"
                      min={320}
                      max={7680}
                      value={customW}
                      onChange={(e) => setCustomW(Number(e.target.value))}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block font-sans text-xs font-medium text-text-secondary mb-1.5">Height</label>
                    <input
                      className={inputCls}
                      type="number"
                      min={240}
                      max={4320}
                      value={customH}
                      onChange={(e) => setCustomH(Number(e.target.value))}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 font-sans text-sm text-text-secondary hover:text-text-primary hover:bg-bg-hover border border-border rounded-md py-[7px] transition-all duration-normal"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-accent text-white font-sans font-medium text-sm rounded-md py-[7px] hover:bg-accent-hover hover:shadow-glow transition-all duration-normal disabled:opacity-50"
                >
                  {creating ? 'Creating…' : 'Create Scene'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-xl font-medium text-text-primary">My Scenes</h2>
        <button
          data-testid="new-scene-btn"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 bg-accent text-white font-sans font-medium text-sm px-3 py-[7px] rounded-md hover:bg-accent-hover hover:shadow-glow transition-all duration-normal"
        >
          <Plus size={14} />
          New Scene
        </button>
      </div>

      {/* Search + Sort row */}
      {scenes.length > 0 && (
        <div className="flex items-center gap-2 mb-5">
          <div className="relative flex-1">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search scenes…"
              data-testid="scene-search-input"
              className="w-full bg-bg-input border border-border text-text-primary rounded-md pl-8 pr-3 py-[7px] text-sm font-sans placeholder:text-text-tertiary hover:bg-bg-hover focus:outline-none focus:border-accent transition-all duration-normal"
            />
          </div>
          <div className="flex items-center gap-1">
            <ArrowsDownUp size={13} className="text-text-tertiary" />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              data-testid="scene-sort-select"
              className="bg-bg-input border border-border text-text-secondary rounded-md px-2 py-[7px] text-xs font-sans focus:outline-none focus:border-accent transition-all duration-normal"
            >
              <option value="updated">Last modified</option>
              <option value="created">Date created</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>
      )}

      {/* Grid */}
      {scenes.length === 0 ? (
        <div className="text-center py-20">
          <p className="font-serif text-xl font-medium text-text-secondary mb-2">No scenes yet</p>
          <p className="font-sans text-sm text-text-tertiary mb-6">Create your first scene to get started</p>
          <button
            onClick={() => setShowCreate(true)}
            className="bg-accent text-white font-sans font-medium text-sm px-4 py-2 rounded-md hover:bg-accent-hover hover:shadow-glow transition-all duration-normal"
          >
            Create your first scene
          </button>
        </div>
      ) : filteredScenes.length === 0 ? (
        <div className="text-center py-16">
          <p className="font-sans text-sm text-text-tertiary">No scenes match "{searchQuery}"</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredScenes.map((scene) => (
            <SceneCard
              key={scene.id}
              scene={scene}
              onOpen={(id) => navigate(`/scene/${id}/edit`)}
              onDelete={handleDelete}
              onRename={handleRename}
              onDuplicate={handleDuplicate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
